import axios from 'axios';
import keycloak, { beginReauth } from '../lib/keycloak';
import config from '../config';
import { qualifiedEndpointPath, SYSTEM_ACCOUNT_SLUG } from '../utils/endpointUrl';
import type {
  BillingPlanChangeResponse,
  BillingSessionRequest,
  BillingSessionResponse,
  PlanCatalogResponse,
} from '../types/billing';
import type { CustomDomain, CustomDomainsView } from '../types/customDomain';
import type { DependentsResponse } from '../types/dependents';
import type { ArchiveEntry } from '../types/archive';
import type {
  AccountAllowedModelGroup,
  AccountModelPolicyRow,
  AccountModelProvider,
  AccountProviderKeySaved,
  AccountProviderKeyVerifyResult,
  MoveAccountModelAgentsResult,
  UpdateAccountModelInput,
} from '../types/accountModels';
import type {
  Agent,
  AgentPrompt,
  AgentMcpConnection,
  AgentJournalFilters,
  AgentJournalsResponse,
  Collaboration,
  CollaborationConfig,
  CollaborationJournal,
  CollaborationNodeData,
  CollaborationStatus,
  Endpoint,
  EndpointRequest,
  EndpointRejectionCode,
  EndpointRequestStatus,
  EndpointResponse,
  EndpointStop,
  Model,
  ModelConfig,
  ModelProvider,
  CreateModelProviderInput,
  UpdateModelProviderInput,
  ModelProviderVerifyResult,
  RemoveModelProviderKeyResult,
  CreateModelInput,
  UpdateModelInput,
  ModelCatalogReview,
  McpConnection,
  MessageRoute,
  KnowledgeDocument,
  KnowledgeBase,
  KnowledgeBaseDocument,
  AgentKnowledgeBase,
  Prompt,
  User,
  SuccessResponse,
  PaginatedResponse,
  CreateAgentInput,
  UpdateAgentInput,
  CreateAgentPromptInput,
  CreateCollaborationInput,
  UpdateCollaborationInput,
  CreateCollaborationConfigInput,
  UpdateCollaborationConfigInput,
  CreateEndpointInput,
  UpdateEndpointInput,
  CreateModelConfigInput,
  UpdateModelConfigInput,
  ModelConfigFilters,
  CreatePromptInput,
  UpdatePromptInput,
  CreateMcpConnectionInput,
  UpdateMcpConnectionInput,
  McpConnectionFilters,
  McpToolsResponse,
  CreateMessageRouteInput,
  UpdateMessageRouteInput,
  MessageRouteFilters,
  CreateKnowledgeDocumentInput,
  UpdateKnowledgeDocumentInput,
  KnowledgeDocumentFilters,
  CreateKnowledgeBaseInput,
  UpdateKnowledgeBaseInput,
  KnowledgeBaseFilters,
} from '@/types';
import type {
  Artifact,
  ArtifactFilters,
  ArtifactListResponse,
  ArtifactVersionDetail,
  BaseArtifactInput,
} from '@/types/artifacts';
import type { HelpAssistantResponse } from '@/types/helpAssistant';
import type { RunDetail } from '@/types/runExplorer';
import { replyFromTurnOutput } from '@/types/assistant';
import type {
  AssistantAvailableConnection,
  AssistantAvailableKnowledgeBase,
  AssistantRequest,
  AssistantThreadDetail,
  AssistantThreadSummary,
  AssistantTurnOutput,
  AssistantTurnResponse,
} from '@/types/assistant';
import type {
  AssistantBaseArtifact,
  AssistantTargetSummary,
} from '@/utils/assistantTargetSummary';
import type {
  GeneratedEndpointUi,
  GeneratedEndpointUiVersion,
  SaveGeneratedUiInput,
  SaveGeneratedUiResponse,
} from '@/types/uiComposer';
import type {
  CompareSuggestion,
  ComposeTestsRequest,
  ComposeTestsResponse,
  CreateEndpointTestCriterionInput,
  CreateEndpointTestInput,
  CreateEndpointTestRunGroupInput,
  CreateEndpointTestRunGroupResult,
  CreateEndpointTestSuiteInput,
  EndpointTest,
  EndpointTestCriterion,
  EndpointTestExportFormat,
  EndpointTestRun,
  EndpointTestRunGroup,
  EndpointTestRunGroupComparison,
  EndpointTestRunGroupDetail,
  EndpointTestRunScore,
  EndpointTestSuite,
  EndpointTestTrendPoint,
  HumanScoreInput,
  ImportEndpointTestsInput,
  ImportEndpointTestsResult,
  RunEndpointTestResult,
  UpdateEndpointTestCriterionInput,
  UpdateEndpointTestInput,
  UpdateEndpointTestSuiteInput,
} from '@/types/endpointTests';
import type {
  AccountVersioningSettings,
  PublishPreview,
  PublishRequest,
  PublishResult,
  ResourceVersionDetail,
  ResourceVersionStatusView,
  ResourceVersionSummary,
  VersionedEntityType,
} from '@/types/versioning';
import type {
  CreateDeploymentInput,
  CreateEnvironmentInput,
  Deployment,
  Environment,
  UpdateEnvironmentInput,
} from '@/types/environments';
import type { EndpointAnalytics, EndpointAnalyticsQuery } from '@/types/analytics';
import type {
  CreateScheduleInput,
  Schedule,
  ScheduleFilters,
  ScheduleRun,
  UpdateScheduleInput,
} from '@/types/schedules';

const api = axios.create({
  baseURL: `${config.API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ─── Missing-account-scope fallback handler ──────────────────────────────────
// AuthContext registers a callback here so the response interceptor can signal
// the "authenticated but no account" state when the backend returns
// 403 MISSING_ACCOUNT_SCOPE. This module imports the keycloak singleton only —
// using a registered callback (rather than importing AuthContext) keeps the
// dependency direction one-way and avoids a circular import.
type MissingAccountScopeHandler = () => void;
let missingAccountScopeHandler: MissingAccountScopeHandler | null = null;

/**
 * Register (or clear, with `null`) the handler invoked when an account-scoped
 * API call is rejected with 403 MISSING_ACCOUNT_SCOPE.
 */
export function setMissingAccountScopeHandler(
  handler: MissingAccountScopeHandler | null
): void {
  missingAccountScopeHandler = handler;
}

// ─── Entitlement error handler ───────────────────────────────────────────────
// Components register a callback here to receive 402 limit_exceeded and
// 403 feature_not_entitled errors from the response interceptor. This avoids
// importing React context into the service layer (circular dep risk).
// Components call `setEntitlementErrorHandler` on mount and pass null on
// unmount to deregister.
import type { EntitlementError } from '../types/billing';
import type {
  AccountSpending,
  AccountSpendingSettings,
  UpdateAccountSpendingInput,
} from '../types/billing';
import {
  isLimitExceededError,
  isFeatureNotEntitledError,
  isNoActivePlanError,
  isSpendingLimitReachedError,
} from '../types/billing';

type EntitlementErrorHandler = (error: EntitlementError) => void;
let entitlementErrorHandler: EntitlementErrorHandler | null = null;

/**
 * Register (or clear, with `null`) the global handler for entitlement
 * enforcement errors (402 limit_exceeded, 403 feature_not_entitled).
 * Only one handler is active at a time — the last registration wins.
 */
export function setEntitlementErrorHandler(
  handler: EntitlementErrorHandler | null
): void {
  entitlementErrorHandler = handler;
}

// ─── Request interceptor: logging + JWT bearer token injection ───────────────
api.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);

    // Inject JWT bearer token from sessionStorage (set and refreshed by AuthContext).
    const token = sessionStorage.getItem('kc_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// ─── Response interceptor: logging, 401 silent refresh, error handling ───────
api.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  async (error) => {
    const errorInfo = {
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message,
    };

    console.error('API Error:', errorInfo);

    // On 401 Unauthorized, attempt a silent token refresh via keycloak-js.
    // The Keycloak singleton is imported from lib/keycloak to avoid a circular
    // dependency with AuthContext.
    if (error.response?.status === 401 && !error.config?._retried) {
      const refreshed = await tryRefreshToken();
      if (refreshed) {
        // Retry the original request once with the new token.
        const newToken = sessionStorage.getItem('kc_token');
        if (newToken) {
          error.config.headers['Authorization'] = `Bearer ${newToken}`;
        }
        // Mark the retry so a second 401 does not loop infinitely.
        error.config._retried = true;
        return api.request(error.config);
      }
      // Refresh failed — redirect to Keycloak login. Guarded (task 1037) so a
      // burst of 401s, or a 401 racing AuthContext's own refresh-failure
      // handling, starts only one redirect.
      if (beginReauth()) {
        redirectToLogin();
      }
    }

    // On 403 MISSING_ACCOUNT_SCOPE specifically, the user is authenticated but
    // has no provisioned AMS account (or a stale token whose claims predate
    // provisioning). Signal AuthContext to render the "account not set up"
    // guard. Match ONLY this code — all other 403s pass through untouched.
    if (
      error.response?.status === 403 &&
      error.response?.data?.code === 'MISSING_ACCOUNT_SCOPE'
    ) {
      missingAccountScopeHandler?.();
    }

    // Handle entitlement enforcement errors:
    //   402 { error: 'limit_exceeded', limit, current, cap }
    //   402 { error: 'no_active_plan', accountId } (task 464/465)
    //   402 { error: 'spending_limit_reached', limitCents, billedCents, period } (task 1159)
    //   403 { error: 'feature_not_entitled', feature }
    // Signal the registered handler so components can render an upgrade prompt
    // (or the choose-a-plan prompt for no_active_plan).
    // These are NOT treated as unrecoverable — they propagate normally after
    // the handler fires so callers can also react locally if needed.
    if (
      error.response?.status === 402 &&
      (isLimitExceededError(error.response?.data) ||
        isNoActivePlanError(error.response?.data) ||
        isSpendingLimitReachedError(error.response?.data))
    ) {
      entitlementErrorHandler?.(error.response.data);
    } else if (
      error.response?.status === 403 &&
      isFeatureNotEntitledError(error.response?.data)
    ) {
      entitlementErrorHandler?.(error.response.data);
    }

    // Enhanced error object for better debugging.
    const enhancedError = new Error(
      error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'An unknown error occurred'
    ) as Error & {
      status?: number;
      response?: unknown;
      url?: string;
      details?: unknown;
      validationErrors?: ValidationErrorDetail[];
    };

    enhancedError.name = 'APIError';
    enhancedError.status = error.response?.status;
    enhancedError.response = error.response?.data;
    enhancedError.url = error.config?.url;
    // Include error details for MCP connection errors.
    enhancedError.details = error.response?.data?.details;
    /**
     * Field-level rejections, kept intact (task 774).
     *
     * A 400 from a definition save names the exact node and field it refused —
     * `nodes[5].inputMapping.targetSummary` and why. Collapsing that to the
     * top-level string, as this did, left the User with a dead Save button and
     * a console line; the editor now renders these rows and navigates to them.
     *
     * Read from `details.validationErrors` FIRST, because that is where the
     * service actually puts them: controllers pass the list as `sendError`'s
     * `details` argument, which nests it. The top-level form is accepted as a
     * fallback so a route that emits it directly still works.
     */
    const rawValidationErrors =
      error.response?.data?.details?.validationErrors ?? error.response?.data?.validationErrors;
    enhancedError.validationErrors = Array.isArray(rawValidationErrors)
      ? (rawValidationErrors as ValidationErrorDetail[])
      : undefined;

    return Promise.reject(enhancedError);
  }
);

/**
 * One field-level rejection from the service's validator (task 774). The
 * `field` is a path into the submitted body — e.g.
 * `nodes[5].inputMapping.targetSummary` — which is what lets the definition
 * editor map a server error back to the node that caused it.
 */
export interface ValidationErrorDetail {
  field: string;
  message: string;
}

// ─── 401 Token Refresh Helpers ────────────────────────────────────────────────

/**
 * Attempts to silently refresh the Keycloak token using the singleton instance.
 * Returns true if a new token was obtained and stored in sessionStorage.
 */
async function tryRefreshToken(): Promise<boolean> {
  if (!keycloak.authenticated) return false;
  try {
    // updateToken(-1) forces refresh unconditionally.
    const refreshed = await keycloak.updateToken(-1);
    if (refreshed && keycloak.token) {
      sessionStorage.setItem('kc_token', keycloak.token);
      if (keycloak.refreshToken) {
        sessionStorage.setItem('kc_refreshToken', keycloak.refreshToken);
      }
      if (keycloak.idToken) {
        sessionStorage.setItem('kc_idToken', keycloak.idToken);
      }
      return true;
    }
    // Token was still valid (not refreshed but not expired).
    return keycloak.authenticated ?? false;
  } catch {
    return false;
  }
}

/**
 * Triggers a Keycloak login redirect via the singleton instance.
 */
function redirectToLogin(): void {
  keycloak.login({ redirectUri: window.location.href });
}

// Helper to build query string
const buildQuery = (params: Record<string, unknown>): string => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      query.append(key, String(value));
    }
  });
  const queryString = query.toString();
  return queryString ? `?${queryString}` : '';
};

// ============================================================================
// Model Configs API - /model-configs (formerly /llm-configs, task 1074)
// ============================================================================
export const modelConfigsApi = {
  getAll: (page = 1, limit = 20, filters?: ModelConfigFilters) =>
    api.get<PaginatedResponse<ModelConfig>>(`/model-configs${buildQuery({ page, limit, ...filters })}`),

  getById: (id: string) =>
    api.get<SuccessResponse<ModelConfig>>(`/model-configs/${id}`),

  getByName: (name: string) =>
    api.get<SuccessResponse<ModelConfig>>(`/model-configs/name/${name}`),

  create: (data: CreateModelConfigInput) =>
    api.post<SuccessResponse<ModelConfig>>('/model-configs', data),

  update: (id: string, data: UpdateModelConfigInput) =>
    api.patch<SuccessResponse<ModelConfig>>(`/model-configs/${id}`, data),

  delete: (id: string) =>
    api.delete<SuccessResponse<ModelConfig>>(`/model-configs/${id}`),

  /**
   * What points at this Model config (task 860). Ungated and account-scoped by
   * default; `scope: 'platform'` counts agents in every account and is answered
   * only for platform staff holding the catalog manage feature, 403 otherwise
   * (task 1169).
   */
  getDependents: (id: string, scope?: 'platform') =>
    api.get<SuccessResponse<DependentsResponse>>(`/model-configs/${id}/dependents`, {
      params: scope ? { scope } : undefined,
    }),
};

// ============================================================================
// Models API - /models (task 1074). Read-only catalog; platform staff only.
// ============================================================================
export const modelsApi = {
  /**
   * Non-retired Models, optionally for one Model Provider. `includeRetired`
   * (task 1104) lists retired ones too — the Models tab only; pickers omit it.
   */
  list: (filters?: { providerId?: string; includeRetired?: boolean }) =>
    api.get<SuccessResponse<Model[]>>(`/models${buildQuery({ ...filters })}`),

  get: (id: string) =>
    api.get<SuccessResponse<Model>>(`/models/${id}`),

  /** Add a Model by hand (task 1102 -> service task 1101). Gated on innovate.be.model-catalog.manage. */
  create: (data: CreateModelInput) =>
    api.post<SuccessResponse<Model>>('/models', data),

  /** Update a Model (task 1093 -> service task 1101). Gated on innovate.be.model-catalog.manage. */
  update: (id: string, data: UpdateModelInput) =>
    api.patch<SuccessResponse<Model>>(`/models/${id}`, data),
};

// ============================================================================
// Model Catalog review - GET /models/review (task 1093 -> service task 1092).
// Platform staff only (innovate.be.models.review). `refresh` bypasses the
// service's per-source caches for one load.
// ============================================================================
export const modelCatalogReviewApi = {
  get: (refresh = false) =>
    api.get<SuccessResponse<ModelCatalogReview>>(`/models/review${refresh ? '?refresh=true' : ''}`),
};

// ============================================================================
// Model Providers API - /model-providers (task 1074). Read-only; platform staff only.
// ============================================================================
export const modelProvidersApi = {
  list: () =>
    api.get<SuccessResponse<ModelProvider[]>>('/model-providers'),

  // Writes (task 1102 -> service task 1101), gated on innovate.be.model-catalog.manage.
  // The API key is write-only: it goes out in these bodies and only its last four come back.
  create: (data: CreateModelProviderInput) =>
    api.post<SuccessResponse<ModelProvider>>('/model-providers', data),

  /** A disable that Model Configs still depend on is refused with 409 naming them. */
  update: (id: string, data: UpdateModelProviderInput) =>
    api.patch<SuccessResponse<ModelProvider>>(`/model-providers/${id}`, data),

  /** Test connection: one minimal request through the provider's protocol. */
  verify: (id: string) =>
    api.post<SuccessResponse<ModelProviderVerifyResult>>(`/model-providers/${id}/verify`),

  /**
   * Remove the stored platform key (task 1166). A seeded provider falls back to
   * its installed key (`fallbackToInstalled: true`); any other provider is left
   * with no key.
   */
  removeKey: (id: string) =>
    api.delete<SuccessResponse<RemoveModelProviderKeyResult>>(`/model-providers/${id}/key`),
};

// ============================================================================
// Agents API - /agents
// ============================================================================
export const agentsApi = {
  getAll: (page = 1, limit = 20, filters?: { isActive?: boolean; search?: string; modelConfigId?: string }) =>
    api.get<PaginatedResponse<Agent>>(`/agents${buildQuery({ page, limit, ...filters })}`),

  getById: (id: string) =>
    api.get<SuccessResponse<Agent>>(`/agents/${id}`),

  create: (data: CreateAgentInput) =>
    api.post<SuccessResponse<Agent>>('/agents', data),

  update: (id: string, data: UpdateAgentInput) =>
    api.patch<SuccessResponse<Agent>>(`/agents/${id}`, data),

  delete: (id: string) =>
    api.delete<SuccessResponse<Agent>>(`/agents/${id}`),

  /**
   * What points at this agent (task 860). `blocking` names the live AI Services
   * that would break, and a non-empty one means DELETE answers `409 IN_USE`.
   */
  getDependents: (id: string) =>
    api.get<SuccessResponse<DependentsResponse>>(`/agents/${id}/dependents`),
};

// ============================================================================
// Agent Prompts API - /agents/:agentId/prompts
// ============================================================================
export const agentPromptsApi = {
  listAgentPrompts: (agentId: string) =>
    api.get<SuccessResponse<AgentPrompt[]>>(`/agents/${agentId}/prompts`),

  getActivePrompt: (agentId: string) =>
    api.get<SuccessResponse<Prompt | null>>(`/agents/${agentId}/prompts/active`),

  assignPrompt: (agentId: string, data: CreateAgentPromptInput) =>
    api.post<SuccessResponse<AgentPrompt>>(`/agents/${agentId}/prompts`, data),

  removePrompt: (agentId: string, promptId: string) =>
    api.delete<SuccessResponse<AgentPrompt>>(`/agents/${agentId}/prompts/${promptId}`),
};

// ============================================================================
// MCP Connections API - /mcp-connections
// ============================================================================
export const mcpConnectionsApi = {
  getAll: (page = 1, limit = 20, filters?: McpConnectionFilters) =>
    api.get<PaginatedResponse<McpConnection>>(`/mcp-connections${buildQuery({ page, limit, ...filters })}`),

  getById: (id: string) =>
    api.get<SuccessResponse<McpConnection>>(`/mcp-connections/${id}`),

  create: (data: CreateMcpConnectionInput) =>
    api.post<SuccessResponse<McpConnection>>('/mcp-connections', data),

  update: (id: string, data: UpdateMcpConnectionInput) =>
    api.patch<SuccessResponse<McpConnection>>(`/mcp-connections/${id}`, data),

  delete: (id: string) =>
    api.delete<SuccessResponse<McpConnection>>(`/mcp-connections/${id}`),

  /** What points at this connection (task 860). Detach-and-proceed; never blocks. */
  getDependents: (id: string) =>
    api.get<SuccessResponse<DependentsResponse>>(`/mcp-connections/${id}/dependents`),

  listTools: (id: string) =>
    api.get<SuccessResponse<McpToolsResponse>>(`/mcp-connections/${id}/tools`),
};

// ============================================================================
// Agent MCP Connections API - /agents/:agentId/mcp-connections
// ============================================================================
export const agentMcpConnectionsApi = {
  list: (agentId: string) =>
    api.get<SuccessResponse<AgentMcpConnection[]>>(`/agents/${agentId}/mcp-connections`),

  add: (agentId: string, mcpConnectionId: string) =>
    api.post<SuccessResponse<AgentMcpConnection>>(`/agents/${agentId}/mcp-connections`, { mcpConnectionId }),

  remove: (agentId: string, mcpConnectionId: string) =>
    api.delete<SuccessResponse<AgentMcpConnection>>(`/agents/${agentId}/mcp-connections/${mcpConnectionId}`),
};

// ============================================================================
// Knowledge Documents API - /knowledge-documents (task 571)
// ----------------------------------------------------------------------------
// Account-level documents: an uploaded file or pasted text. Two create shapes
// share one route — JSON with `content` for pasted text, multipart with a `file`
// part for an upload. The envelope (docx/pdf/xlsx/txt/md/csv, 10 MB) is enforced
// server-side; `src/config/index.ts` carries the same values so the UI can state
// them before a User spends an upload.
//
// Deleting a document removes it EVERYWHERE — its chunks and every knowledge-base
// membership go with it.
// ============================================================================
export const knowledgeDocumentsApi = {
  getAll: (page = 1, limit = 20, filters?: KnowledgeDocumentFilters) =>
    api.get<PaginatedResponse<KnowledgeDocument>>(
      `/knowledge-documents${buildQuery({ page, limit, ...filters })}`
    ),

  getById: (id: string) =>
    api.get<SuccessResponse<KnowledgeDocument>>(`/knowledge-documents/${id}`),

  /**
   * The document's original file as a Blob (task 1081): uploaded bytes under
   * their stored type, or pasted text as `text/plain`. `download: true` asks
   * the service for an attachment disposition (`?download=1`).
   */
  getFile: (id: string, options?: { download?: boolean }): Promise<Blob> =>
    api
      .get<Blob>(`/knowledge-documents/${id}/file${options?.download ? '?download=1' : ''}`, {
        responseType: 'blob',
      })
      .then((res) => res.data),

  /** Pasted-text create (application/json). */
  createFromText: (data: CreateKnowledgeDocumentInput) =>
    api.post<SuccessResponse<KnowledgeDocument>>('/knowledge-documents', data),

  /**
   * File upload (multipart/form-data). The Content-Type header is stated for
   * intent only — axios replaces it with the boundary-carrying value the
   * browser generates for a FormData body.
   */
  upload: (
    file: File,
    fields: { name: string; description?: string },
    /**
     * What to do when the name is already taken (task 1084). Absent on the
     * first attempt, so a collision answers 409 `NAME_CONFLICT`; the resend
     * after the User chooses carries `replace` or `keep-both`.
     */
    onConflict?: 'replace' | 'keep-both'
  ) => {
    const form = new FormData();
    form.append('file', file);
    form.append('name', fields.name);
    if (fields.description) {
      form.append('description', fields.description);
    }
    if (onConflict) {
      form.append('onConflict', onConflict);
    }
    return api.post<SuccessResponse<KnowledgeDocument>>('/knowledge-documents', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /**
   * Swap a document's stored file in place (task 1084): same id, same
   * description and knowledge base memberships, status back to `pending` while
   * it is re-indexed.
   */
  replaceContent: (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api
      .put<SuccessResponse<KnowledgeDocument>>(`/knowledge-documents/${id}/content`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((res) => res.data.data);
  },

  update: (id: string, data: UpdateKnowledgeDocumentInput) =>
    api.patch<SuccessResponse<KnowledgeDocument>>(`/knowledge-documents/${id}`, data),

  delete: (id: string) =>
    api.delete<SuccessResponse<KnowledgeDocument>>(`/knowledge-documents/${id}`),
};

// ============================================================================
// Knowledge Bases API - /knowledge-bases (task 571)
// ----------------------------------------------------------------------------
// A base is a grouping, not a container: `addDocument`/`removeDocument` manage
// membership rows and never touch the document or its chunks. Deleting a base
// deletes the grouping only — its documents survive as unfiled library entries.
// ============================================================================
export const knowledgeBasesApi = {
  getAll: (page = 1, limit = 20, filters?: KnowledgeBaseFilters) =>
    api.get<PaginatedResponse<KnowledgeBase>>(
      `/knowledge-bases${buildQuery({ page, limit, ...filters })}`
    ),

  getById: (id: string) =>
    api.get<SuccessResponse<KnowledgeBase>>(`/knowledge-bases/${id}`),

  create: (data: CreateKnowledgeBaseInput) =>
    api.post<SuccessResponse<KnowledgeBase>>('/knowledge-bases', data),

  update: (id: string, data: UpdateKnowledgeBaseInput) =>
    api.patch<SuccessResponse<KnowledgeBase>>(`/knowledge-bases/${id}`, data),

  delete: (id: string) =>
    api.delete<SuccessResponse<KnowledgeBase>>(`/knowledge-bases/${id}`),

  /** What points at this base (task 860). Detach-and-proceed; never blocks. */
  getDependents: (id: string) =>
    api.get<SuccessResponse<DependentsResponse>>(`/knowledge-bases/${id}/dependents`),

  /** Membership rows, each with its populated `document`. */
  listDocuments: (id: string) =>
    api.get<SuccessResponse<KnowledgeBaseDocument[]>>(`/knowledge-bases/${id}/documents`),

  addDocument: (id: string, documentId: string) =>
    api.post<SuccessResponse<KnowledgeBaseDocument>>(`/knowledge-bases/${id}/documents`, {
      documentId,
    }),

  removeDocument: (id: string, documentId: string) =>
    api.delete<SuccessResponse<KnowledgeBaseDocument>>(
      `/knowledge-bases/${id}/documents/${documentId}`
    ),
};

// ============================================================================
// Agent Knowledge Bases API - /agents/:agentId/knowledge-bases (task 573)
// ----------------------------------------------------------------------------
// The standing knowledge an agent always searches. Mirrors
// agentMcpConnectionsApi exactly, including the both-parents account check the
// service applies. An agent with several attached bases searches all of them.
// ============================================================================
/**
 * One agent's run history, optionally scoped to a single AI Service's flow
 * (task 797). `collaborationConfigId` is the service scope — a Collaboration is
 * a single RUN, so `collaborationId` narrows to one execution instead.
 */
export const agentJournalsApi = {
  list: (agentId: string, filters?: AgentJournalFilters) =>
    api.get<SuccessResponse<AgentJournalsResponse>>(
      `/agents/${agentId}/journals${buildQuery({ ...filters })}`
    ),
};

export const agentKnowledgeBasesApi = {
  list: (agentId: string) =>
    api.get<SuccessResponse<AgentKnowledgeBase[]>>(`/agents/${agentId}/knowledge-bases`),

  add: (agentId: string, knowledgeBaseId: string) =>
    api.post<SuccessResponse<AgentKnowledgeBase>>(`/agents/${agentId}/knowledge-bases`, {
      knowledgeBaseId,
    }),

  remove: (agentId: string, knowledgeBaseId: string) =>
    api.delete<SuccessResponse<AgentKnowledgeBase>>(
      `/agents/${agentId}/knowledge-bases/${knowledgeBaseId}`
    ),
};

// ============================================================================
// Prompts API - /prompts
// ============================================================================
export const promptsApi = {
  getAll: (page = 1, limit = 20, filters?: { isActive?: string; search?: string }) =>
    api.get<PaginatedResponse<Prompt>>(`/prompts${buildQuery({ page, limit, ...filters })}`),

  getById: (id: string) =>
    api.get<SuccessResponse<Prompt>>(`/prompts/${id}`),

  create: (data: CreatePromptInput) =>
    api.post<SuccessResponse<Prompt>>('/prompts', data),

  update: (id: string, data: UpdatePromptInput) =>
    api.patch<SuccessResponse<Prompt>>(`/prompts/${id}`, data),

  delete: (id: string) =>
    api.delete<SuccessResponse<Prompt>>(`/prompts/${id}`),

  /** What points at this prompt (task 860). Detach-and-proceed; never blocks. */
  getDependents: (id: string) =>
    api.get<SuccessResponse<DependentsResponse>>(`/prompts/${id}/dependents`),

  // Get agents using this prompt
  getAgentsUsingPrompt: (promptId: string) =>
    api.get<SuccessResponse<AgentPrompt[]>>(`/prompts/${promptId}/agents`),
};

// ============================================================================
// Collaboration Configs API - /collaboration-configs
// ============================================================================
export const collaborationConfigsApi = {
  getAll: (page = 1, limit = 20, filters?: { search?: string }) =>
    api.get<PaginatedResponse<CollaborationConfig>>(`/collaboration-configs${buildQuery({ page, limit, ...filters })}`),

  getById: (id: string) =>
    api.get<SuccessResponse<CollaborationConfig>>(`/collaboration-configs/${id}`),

  create: (data: CreateCollaborationConfigInput) =>
    api.post<SuccessResponse<CollaborationConfig>>('/collaboration-configs', data),

  update: (id: string, data: UpdateCollaborationConfigInput) =>
    api.patch<SuccessResponse<CollaborationConfig>>(`/collaboration-configs/${id}`, data),

  delete: (id: string) =>
    api.delete<SuccessResponse<CollaborationConfig>>(`/collaboration-configs/${id}`),

  /**
   * What points at this config (task 860). `blocking` names the live AI Services
   * that would break, and a non-empty one means DELETE answers `409 IN_USE`.
   */
  getDependents: (id: string) =>
    api.get<SuccessResponse<DependentsResponse>>(`/collaboration-configs/${id}/dependents`),
};

// ============================================================================
// Collaborations API - /collaborations
// ============================================================================
export const collaborationsApi = {
  getAll: (page = 1, limit = 20, filters?: { configId?: string; requestId?: string; status?: string; queueId?: string; search?: string; endpointId?: string; from?: string; to?: string; environment?: string; includeTests?: boolean; sortBy?: 'startedAt' | 'duration' | 'cost'; sortDir?: 'asc' | 'desc' }) =>
    api.get<PaginatedResponse<Collaboration>>(`/collaborations${buildQuery({ page, limit, ...filters })}`),

  getById: (id: string) =>
    api.get<SuccessResponse<Collaboration>>(`/collaborations/${id}`),

  create: (data: CreateCollaborationInput) =>
    api.post<SuccessResponse<Collaboration>>('/collaborations', data),

  update: (id: string, data: UpdateCollaborationInput) =>
    api.patch<SuccessResponse<Collaboration>>(`/collaborations/${id}`, data),

  // NO delete (tasks 859, 864). A Collaboration is a RUN — a record of what
  // happened, not a resource a User built — so the service removed the route
  // and the app removed the control. `DELETE /api/collaborations/:id` now
  // answers `400 RESERVED_PATH`, because with the route gone the two-segment
  // path falls through to the dynamic-endpoint resolver.

  // Nested resources
  getJournals: (collaborationId: string, page = 1, limit = 20, filters?: { nodeType?: string; event?: string; agentId?: string; nodeId?: string }) =>
    api.get<PaginatedResponse<CollaborationJournal>>(
      `/collaborations/${collaborationId}/journals${buildQuery({ page, limit, ...filters })}`
    ),

  getJournal: (collaborationId: string, journalId: string) =>
    api.get<SuccessResponse<CollaborationJournal>>(
      `/collaborations/${collaborationId}/journals/${journalId}`
    ),

  getStatuses: (collaborationId: string, page = 1, limit = 20, filters?: { status?: string }) =>
    api.get<PaginatedResponse<CollaborationStatus>>(
      `/collaborations/${collaborationId}/statuses${buildQuery({ page, limit, ...filters })}`
    ),

  getStatus: (collaborationId: string, statusId: string) =>
    api.get<SuccessResponse<CollaborationStatus>>(
      `/collaborations/${collaborationId}/statuses/${statusId}`
    ),

  getNodeData: (collaborationId: string) =>
    api.get<SuccessResponse<CollaborationNodeData[]>>(
      `/collaborations/${collaborationId}/node-data`
    ),
};

// ============================================================================
// Run Explorer API (task 1151) - /collaborations/:collaborationId/runs/:runId
// ============================================================================
// One read for the whole run (task 1150, step 6): the collaboration, the
// definition it ran, every journal entry without its large text fields, the
// node data, and the parent and child runs. A Collaboration row IS a run, so
// the app passes the same id twice until the service says otherwise. The large
// fields come from the per-entry route, `collaborationsApi.getJournal`.
export const runExplorerApi = {
  getRunDetail: (collaborationId: string, runId: string) =>
    api.get<SuccessResponse<RunDetail>>(`/collaborations/${collaborationId}/runs/${runId}`),
};

// ============================================================================
// Endpoints API - /endpoints
// ============================================================================
export const endpointsApi = {
  getAll: (page = 1, limit = 20, filters?: { method?: string; collaborationConfigId?: string; search?: string }) =>
    api.get<PaginatedResponse<Endpoint>>(`/endpoints${buildQuery({ page, limit, ...filters })}`),

  getById: (id: string) =>
    api.get<SuccessResponse<Endpoint>>(`/endpoints/${id}`),

  create: (data: CreateEndpointInput) =>
    api.post<SuccessResponse<Endpoint>>('/endpoints', data),

  update: (id: string, data: UpdateEndpointInput) =>
    api.patch<SuccessResponse<Endpoint>>(`/endpoints/${id}`, data),

  delete: (id: string) =>
    api.delete<SuccessResponse<Endpoint>>(`/endpoints/${id}`),

  // Stop and resume (task 1163). Both answer the updated endpoint read shape,
  // whose `stop` field is the open episode or null. The reason is optional and
  // stays inside the account: the 409 a caller gets never carries it.
  stopEndpoint: (id: string, reason?: string) =>
    api.post<SuccessResponse<Endpoint>>(`/endpoints/${id}/stop`, { reason: reason ?? '' }),

  resumeEndpoint: (id: string) =>
    api.post<SuccessResponse<Endpoint>>(`/endpoints/${id}/resume`, {}),

  // Every stop episode, most recent first. A ledger: rows are never deleted.
  listEndpointStops: (id: string) =>
    api.get<SuccessResponse<EndpointStop[]>>(`/endpoints/${id}/stops`),

  // Nested resources - requests
  //
  // `status` defaults to 'accepted': innovate-service now captures gate-rejected
  // calls too (task 717), and the Runs views are about runs — a refused call has
  // no run behind it, so counting it would inflate every "N requests" figure in
  // the app. Pass status: null to ask for everything.
  getRequests: (
    endpointId: string,
    page = 1,
    limit = 20,
    status: EndpointRequestStatus | null = 'accepted',
    rejectionCode?: EndpointRejectionCode
  ) =>
    api.get<PaginatedResponse<EndpointRequest>>(
      `/endpoints/${endpointId}/requests${buildQuery({
        page,
        limit,
        ...(status ? { status } : {}),
        ...(rejectionCode ? { rejectionCode } : {}),
      })}`
    ),

  getRequest: (endpointId: string, requestId: string) =>
    api.get<SuccessResponse<EndpointRequest>>(
      `/endpoints/${endpointId}/requests/${requestId}`
    ),

  // Nested resources - responses
  getResponses: (endpointId: string, requestId: string, page = 1, limit = 20) =>
    api.get<PaginatedResponse<EndpointResponse>>(
      `/endpoints/${endpointId}/requests/${requestId}/responses${buildQuery({ page, limit })}`
    ),

  getResponse: (endpointId: string, requestId: string, responseId: string) =>
    api.get<SuccessResponse<EndpointResponse>>(
      `/endpoints/${endpointId}/requests/${requestId}/responses/${responseId}`
    ),
};

// ============================================================================
// Users API - /users
// ============================================================================
export const usersApi = {
  getAll: (page = 1, limit = 20) =>
    api.get<PaginatedResponse<User>>(`/users${buildQuery({ page, limit })}`),

  getById: (id: string) =>
    api.get<SuccessResponse<User>>(`/users/${id}`),
};

// ============================================================================
// Message Routes API - /message-routes
// ============================================================================
export const messageRoutesApi = {
  getAll: (page = 1, limit = 20, filters?: MessageRouteFilters) =>
    api.get<PaginatedResponse<MessageRoute>>(`/message-routes${buildQuery({ page, limit, ...filters })}`),

  getById: (id: string) =>
    api.get<SuccessResponse<MessageRoute>>(`/message-routes/${id}`),

  create: (data: CreateMessageRouteInput) =>
    api.post<SuccessResponse<MessageRoute>>('/message-routes', data),

  update: (id: string, data: UpdateMessageRouteInput) =>
    api.patch<SuccessResponse<MessageRoute>>(`/message-routes/${id}`, data),

  delete: (id: string) =>
    api.delete<SuccessResponse<MessageRoute>>(`/message-routes/${id}`),
};

// ============================================================================
// Dynamic Endpoints API - /{accountSlug}/{path} (task 385)
// ----------------------------------------------------------------------------
// Endpoints are namespaced by the owning account's slug. The slug ALWAYS comes
// from the endpoint row being invoked (`endpoint.accountSlug`), never from the
// caller's token — that is what makes calling a platform endpoint (System
// account, slug `mw`) work through the same code path. A null/undefined slug
// falls back to the legacy root shape, which the service still resolves during
// the transition window; see src/utils/endpointUrl.ts for that decision.
//
// `invokeWithId` (the `/{path}/{id}` resource form) was removed here: it had no
// callers anywhere in the app, so slug-qualifying it would have shipped an
// untested second builder.
// ============================================================================
export const dynamicEndpointsApi = {
  /**
   * `environment` (task 760) inserts the deployment environment segment, so the
   * call resolves to exactly what that stage serves — the same path production
   * traffic takes. Omit it to address the working state, or — where the account
   * is publishing — the released version of the default `/` environment.
   */
  invoke: <T = unknown>(
    accountSlug: string | null | undefined,
    path: string,
    method: string,
    data?: unknown,
    environment?: string | null
  ) => {
    const config = {
      method: method.toLowerCase(),
      url: `/${qualifiedEndpointPath(accountSlug, path, environment)}`,
      data,
    };
    return api.request<SuccessResponse<T>>(config);
  },
};

// ============================================================================
// Help Assistant API - dynamic endpoint /mw/help-assistant (task 544/545)
// Another System-account platform AI Service, addressed like any customer
// endpoint. Because the endpoint is System-owned and the caller is a real User,
// the service classifies the call as a platform session: it is billed to the
// System account, so asking for help never consumes the User's plan quota.
// ============================================================================
export const helpAssistantApi = {
  // Ask the help assistant a question. Omit collaborationId to start a new
  // conversation; pass it to continue an existing one.
  sendMessage: (data: { message: string; collaborationId?: string }) =>
    api.post<SuccessResponse<HelpAssistantResponse>>(
      `/${SYSTEM_ACCOUNT_SLUG}/help-assistant`,
      data
    ),
};

// ============================================================================
// Assistant API — the standard invoke path (tasks 720, 737, 739)
// ----------------------------------------------------------------------------
// Reports: innovate-app-persistent-assistant-sidebar.md,
//          innovate-service-assistant-standard-endpoint.md,
//          innovate-service-endpoint-threads.md
//
// The Assistant is an ordinary AI Service on the System account, invoked
// exactly like the Composer and the help assistant beside it. It used to have a
// dedicated `POST /threads/assistant` route backed by a bespoke dispatcher
// process; that is retired (task 738). The dispatch now happens INSIDE the
// endpoint's collaboration: a classifier node runs on every turn and a decision
// node routes it to the help assistant or the Composer build flow.
//
// Because the run is a platform session (System-owned config, real User), it is
// billed to the System account — asking for help never consumes the User's plan
// quota.
//
// The conversation is addressed by `threadId`, never a `collaborationId`. That
// is the whole point of the Threads primitive: a thread outlives the executions
// that carry its turns (a failed run, a redeploy, a specialist switch), and a
// leaked thread id is inert because reading or continuing one always requires
// the matching consumer identity — here, the caller's JWT.
// ============================================================================

/**
 * The Assistant's endpoint path — the one string the sidebar's turns and its
 * thread listing must agree on (task 805).
 *
 * A thread can only be continued on the AI Service it belongs to, so the list a
 * chat history is drawn from has to be scoped to the same AI Service the turns
 * are sent to. Spelling it once means the two cannot drift: `sendMessage` POSTs
 * here, and `threadsApi.list` scopes here.
 */
export const ASSISTANT_ENDPOINT_PATH = `${SYSTEM_ACCOUNT_SLUG}/assistant`;

/**
 * The turn as the standard invoke path takes it.
 *
 * The page/entity context envelope is FLAT on the wire: on a dynamic endpoint
 * those are declared fields of the endpoint's own input schema, not a bespoke
 * `context` property. `threadId` and `newThread` are the Threads primitive's
 * reserved body keys, stripped by the service before schema validation.
 */
interface AssistantInvokeBody {
  message: string;
  location: string;
  /**
   * The resource the User is looking at (task 746). Generalized from the former
   * `endpointId` field, which the service still accepts as a deprecated alias
   * for `{ resourceType: 'endpoint', resourceId }` — the app no longer sends it.
   */
  resourceType?: string;
  resourceId?: string;
  /**
   * The target AI Service's current state, and the interface to revise (task
   * 813). Both are declared fields of the Assistant's turn input, both are
   * OPTIONAL, and both are sent only on a turn that has a resource — the branch
   * mappings read them off the entry node, so a general turn simply resolves
   * them to undefined and the answer tier never sees them.
   */
  targetSummary?: AssistantTargetSummary;
  /**
   * What the turn revises: the target's saved interface, or — since task 828 —
   * a file the User pointed at. One declared field, because the engine reads it
   * the same way for both and a second key would be a second thing for every
   * definition to map.
   */
  baseArtifact?: AssistantBaseArtifact | BaseArtifactInput;
  /**
   * The account's active MCP connections, name and description only (task 961).
   * A declared field of the Assistant's turn input like the two above, mapped
   * off the entry node into both Composer guide nodes. Sent only when the
   * account has at least one, so a turn for an account with none — or before
   * the list resolves — is byte-identical to what it was before.
   */
  availableConnections?: AssistantAvailableConnection[];
  /**
   * The account's knowledge bases, name, description and document count only
   * (task 1079). Declared turn input mapped into both Composer guide nodes,
   * exactly like `availableConnections`, and sent only when the account has at
   * least one, so a turn for an account with none — or before the list
   * resolves — is byte-identical to what it was before.
   */
  availableKnowledgeBases?: AssistantAvailableKnowledgeBase[];
  /**
   * The document this turn is ABOUT (task 970) — a file the User attached to the
   * sidebar conversation, uploaded through `knowledgeDocumentsApi.upload` and so
   * owned by their own account.
   *
   * A RESERVED invocation key, like `threadId` and `newThread`: the service
   * strips it before schema validation, checks it belongs to the calling account
   * and is `ready`, and puts it on the run context for THAT TURN ONLY. It is
   * never persisted on the thread, which is why the sidebar re-sends it on every
   * turn while the attachment chip is up.
   */
  documentId?: string;
  threadId?: string;
  newThread?: boolean;
}

/**
 * The standard invoke response. Every dynamic endpoint answers in this shape;
 * the turn output arrives nested under `output` rather than as a bespoke
 * top-level body, with `specialist` naming the branch the routing decision chose
 * — so it can never disagree with which branch ran — and each contributing
 * node's reply object under that node's id (task 763).
 */
interface AssistantInvokeResponse {
  requestId: string;
  collaborationId?: string;
  threadId?: string;
  status: string;
  output: AssistantTurnOutput | null;
}

export const assistantApi = {
  /**
   * POST /api/{SYSTEM_ACCOUNT_SLUG}/assistant — one Assistant turn.
   *
   * Omit `threadId` for the first turn of a fresh chat; pass `newThread: true`
   * for "New chat" (the old threads stay retrievable).
   *
   * The standard response is unwrapped here so the rest of the app keeps
   * speaking `AssistantTurnResponse`: `specialist` beside the reply, which is
   * what lets a card say "via Composer" without the specialist having to know
   * it is being dispatched. Reading the reply OUT of the turn output is
   * `replyFromTurnOutput`'s job — one seam, shared with transcript replay.
   */
  sendMessage: async (data: AssistantRequest): Promise<AssistantTurnResponse> => {
    const body: AssistantInvokeBody = {
      message: data.message,
      location: data.context.location,
      ...(data.context.resourceType && data.context.resourceId
        ? { resourceType: data.context.resourceType, resourceId: data.context.resourceId }
        : {}),
      // The target's current state rides the same turn as the resource pair that
      // identifies it (task 813). The caller decides whether there is one; this
      // seam only refuses to send an empty key, so a general turn's body is
      // byte-identical to what it was before.
      ...(data.targetSummary ? { targetSummary: data.targetSummary } : {}),
      ...(data.baseArtifact ? { baseArtifact: data.baseArtifact } : {}),
      // Same seam rule as the two above: an empty list is not sent at all, so
      // the guide's fallback covers an account with no connections rather than
      // being handed an empty array to reason about (task 961).
      ...(data.availableConnections && data.availableConnections.length > 0
        ? { availableConnections: data.availableConnections }
        : {}),
      // The account's knowledge bases, same seam rule (task 1079): an empty list
      // is not sent, so the guide's no-bases fallback covers an account with none
      // rather than being handed an empty array to reason about.
      ...(data.availableKnowledgeBases && data.availableKnowledgeBases.length > 0
        ? { availableKnowledgeBases: data.availableKnowledgeBases }
        : {}),
      // The attached document, flattened onto the body beside `threadId` — both
      // are reserved keys the service strips before validating (task 970). Same
      // seam rule as the fields above: absent unless there is one, so a turn
      // with no attachment is byte-identical to what it was before.
      ...(data.documentId ? { documentId: data.documentId } : {}),
      ...(data.threadId ? { threadId: data.threadId } : {}),
      ...(data.newThread ? { newThread: true } : {}),
    };

    const response = await api.post<SuccessResponse<AssistantInvokeResponse>>(
      `/${ASSISTANT_ENDPOINT_PATH}`,
      body
    );

    const invoked = response.data.data;
    const { specialist, reply } = replyFromTurnOutput(invoked.output);

    return {
      threadId: invoked.threadId ?? '',
      specialist,
      reply: reply ?? {},
      // The run that produced the turn, for the reply card's View run link (task 1151).
      ...(invoked.collaborationId ? { collaborationId: invoked.collaborationId } : {}),
    };
  },
};

export const threadsApi = {
  /**
   * GET /api/threads — the calling User's threads on ONE AI Service, most recent
   * activity first. A plain success envelope, not a paginated one: the service
   * caps the list server-side rather than paging it.
   *
   * `endpointPath` is REQUIRED, by the service and by this signature (task 805).
   * It used to be absent, and the list then spanned every AI Service the User
   * had ever talked to — including conversations that could not be continued
   * from the sidebar, because continuation is endpoint-scoped: opening one
   * rendered "No content" and sending to it answered "Thread not found". Typed
   * as required rather than defaulted here so the caller states which AI Service
   * its conversation lives on, the same way it states where it sends turns.
   *
   * The two resource filters (task 745) are mutually exclusive, because they
   * answer opposite questions: `resourceType` + `resourceId` asks for the
   * conversations that belong to one resource, `general: true` for the ones that
   * belong to no resource at all. Sending both is a 400 — callers pass one or
   * the other.
   */
  list: (filter: {
    endpointPath: string;
    resourceType?: string;
    resourceId?: string;
    general?: boolean;
  }) => api.get<SuccessResponse<AssistantThreadSummary[]>>(`/threads${buildQuery({ ...filter })}`),

  /** GET /api/threads/:id — one thread with its full transcript. */
  get: (threadId: string) =>
    api.get<SuccessResponse<AssistantThreadDetail>>(`/threads/${threadId}`),

  /** PATCH /api/threads/:id — rename a chat; `title` is the only writable field. */
  rename: (threadId: string, title: string) =>
    api.patch<SuccessResponse<AssistantThreadSummary>>(`/threads/${threadId}`, { title }),

  /** POST /api/threads/:id/archive — retire a chat from the history list. */
  archive: (threadId: string) =>
    api.post<SuccessResponse<AssistantThreadSummary>>(`/threads/${threadId}/archive`),
};

// ============================================================================
// Generated Endpoint UIs API - /generated-endpoint-uis (task 124)
// Durable, versioned storage for generated UIs, modelled as a parent UI holding
// a chain of versions. The parent says *which endpoint*; the version holds the
// `source` and `prompt`.
//
// Mind the id asymmetry on the `:id` routes — they take different kinds of id:
//   GET    /generated-endpoint-uis/:id  → a VERSION id (handleGetGeneratedUiVersion)
//   DELETE /generated-endpoint-uis/:id  → a PARENT  id (handleDeleteGeneratedUi,
//                                          soft-deletes the UI and all versions)
// Passing the wrong one 404s. Reading the version's fields off the parent was
// the task-396 bug; the types below now make that a compile error.
// ============================================================================
export const generatedUiApi = {
  // List parent UIs (each with its nested `versions`), optionally scoped to one endpoint.
  list: (endpointId?: string) =>
    api.get<PaginatedResponse<GeneratedEndpointUi>>(
      `/generated-endpoint-uis${buildQuery(endpointId ? { endpointId } : {})}`
    ),

  /** Fetch a single saved version by its **version** id. */
  getById: (id: string) =>
    api.get<SuccessResponse<GeneratedEndpointUiVersion>>(`/generated-endpoint-uis/${id}`),

  save: (data: SaveGeneratedUiInput) =>
    api.post<SuccessResponse<SaveGeneratedUiResponse>>('/generated-endpoint-uis', data),

  /** Soft-delete a UI and hide its versions. `id` is the **parent** id. */
  delete: (id: string) =>
    api.delete<SuccessResponse<GeneratedEndpointUi>>(`/generated-endpoint-uis/${id}`),

  /**
   * Pin a version to the AI Service's public page (task 847). `id` is the
   * **parent** id; `versionId` names the version to serve, defaulting to the
   * current head. One call sets both pointers the public route reads — the
   * parent's `publishedVersionId` and the endpoint's `publishedUiId` — so the
   * app never has to keep two writes in step.
   *
   * Publishing does NOT flip `uiExposed`: pinning a version and putting a page
   * on the internet are separate decisions, and stay separate switches.
   */
  publish: (id: string, versionId?: string) =>
    api.post<SuccessResponse<GeneratedEndpointUi>>(
      `/generated-endpoint-uis/${id}/publish`,
      versionId ? { versionId } : {}
    ),

  /** Take the public page down — clears both pointers, leaves every version. */
  unpublish: (id: string) =>
    api.delete<SuccessResponse<GeneratedEndpointUi>>(`/generated-endpoint-uis/${id}/publish`),
};

// ============================================================================
// Endpoint Tests API - /endpoint-tests (task 363/365)
// Saved, reusable test inputs per endpoint. `run(id)` executes the test's
// saved input against the endpoint and records an EndpointTestRun (422 for
// responseQueued endpoints). DELETE soft-deletes and returns the deleted test.
// ============================================================================
export const endpointTestsApi = {
  list: (endpointId: string, page = 1, limit = 20) =>
    api.get<PaginatedResponse<EndpointTest>>(
      `/endpoint-tests${buildQuery({ endpointId, page, limit })}`
    ),

  getById: (id: string) =>
    api.get<SuccessResponse<EndpointTest>>(`/endpoint-tests/${id}`),

  create: (data: CreateEndpointTestInput) =>
    api.post<SuccessResponse<EndpointTest>>('/endpoint-tests', data),

  update: (id: string, data: UpdateEndpointTestInput) =>
    api.patch<SuccessResponse<EndpointTest>>(`/endpoint-tests/${id}`, data),

  delete: (id: string) =>
    api.delete<SuccessResponse<EndpointTest>>(`/endpoint-tests/${id}`),

  run: (id: string) =>
    api.post<SuccessResponse<RunEndpointTestResult>>(`/endpoint-tests/${id}/run`),

  listRuns: (id: string, page = 1, limit = 20) =>
    api.get<PaginatedResponse<EndpointTestRun>>(
      `/endpoint-tests/${id}/runs${buildQuery({ page, limit })}`
    ),

  /**
   * Bulk create from a parsed file (task 1173/1175). The browser parses the
   * CSV/JSON; the service maps each row through `columnMap`, skips rows with no
   * mapped input field, and creates with `source: 'imported'`.
   */
  import: (data: ImportEndpointTestsInput) =>
    api.post<SuccessResponse<ImportEndpointTestsResult>>('/endpoint-tests/import', data),

  /**
   * The tests list as a file, in the import column shape. Read as a blob through
   * this client so the bearer token rides along (the `downloadFile` rationale).
   */
  exportFile: (endpointId: string, format: EndpointTestExportFormat) =>
    api.get<Blob>(`/endpoint-tests${buildQuery({ endpointId, format })}`, {
      responseType: 'blob',
    }),

  /** The test's last five results across test runs, newest first (task 1172). */
  trend: (id: string) =>
    api.get<SuccessResponse<EndpointTestTrendPoint[]>>(`/endpoint-tests/${id}/trend`),

  /** Test-scoped criteria (task 1179). */
  listCriteria: (id: string) =>
    api.get<SuccessResponse<EndpointTestCriterion[]>>(`/endpoint-tests/${id}/criteria`),

  createCriterion: (id: string, data: CreateEndpointTestCriterionInput) =>
    api.post<SuccessResponse<EndpointTestCriterion>>(`/endpoint-tests/${id}/criteria`, data),
};

// ============================================================================
// Test runs - /endpoint-test-run-groups (tasks 1172, 1173, 1174; UI 1176-1178)
// ----------------------------------------------------------------------------
// One test run (`EndpointTestRunGroup`) is the tests that executed together,
// where, and how. Creating one EXECUTES it: the 202 carries the id and the
// results fill in asynchronously, so the detail read is polled until settled.
// `compare` is a fixed path and the service registers it before `/:id`.
// ============================================================================
export const endpointTestRunGroupsApi = {
  /** `endpointTestId` narrows to test runs that contain that test. */
  list: (endpointId: string, page = 1, limit = 20, endpointTestId?: string) =>
    api.get<PaginatedResponse<EndpointTestRunGroup>>(
      `/endpoint-test-run-groups${buildQuery({ endpointId, endpointTestId, page, limit })}`
    ),

  getById: (id: string) =>
    api.get<SuccessResponse<EndpointTestRunGroupDetail>>(`/endpoint-test-run-groups/${id}`),

  create: (data: CreateEndpointTestRunGroupInput) =>
    api.post<SuccessResponse<CreateEndpointTestRunGroupResult>>('/endpoint-test-run-groups', data),

  delete: (id: string) =>
    api.delete<SuccessResponse<EndpointTestRunGroup>>(`/endpoint-test-run-groups/${id}`),

  /** One row per result, as a file (blob, same reason as the tests export). */
  exportFile: (id: string, format: EndpointTestExportFormat) =>
    api.get<Blob>(`/endpoint-test-run-groups/${id}/export${buildQuery({ format })}`, {
      responseType: 'blob',
    }),

  compare: (a: string, b: string) =>
    api.get<SuccessResponse<EndpointTestRunGroupComparison>>(
      `/endpoint-test-run-groups/compare${buildQuery({ a, b })}`
    ),

  compareExportFile: (a: string, b: string, format: EndpointTestExportFormat) =>
    api.get<Blob>(`/endpoint-test-run-groups/compare${buildQuery({ a, b, format })}`, {
      responseType: 'blob',
    }),

  /** The default baseline for a test run, or `{ id: null }`. */
  compareSuggestion: (id: string) =>
    api.get<SuccessResponse<CompareSuggestion>>(
      `/endpoint-test-run-groups/${id}/compare-suggestion`
    ),
};

// ============================================================================
// Results - /endpoint-test-runs (tasks 1172, 1179)
// The human verdict is the result's single `human` score row (PUT replaces
// it); `scores` lists every check, graded criterion and human score.
// ============================================================================
export const endpointTestRunsApi = {
  setHumanScore: (id: string, data: HumanScoreInput) =>
    api.put<SuccessResponse<EndpointTestRunScore>>(`/endpoint-test-runs/${id}/human-score`, data),

  listScores: (id: string) =>
    api.get<SuccessResponse<EndpointTestRunScore[]>>(`/endpoint-test-runs/${id}/scores`),
};

// ============================================================================
// Criteria - /endpoint-test-criteria, /endpoints/:id/test-criteria (task 1179)
// Test-scoped criteria are listed and created under their test
// (`endpointTestsApi.listCriteria`); service criteria under the endpoint.
// ============================================================================
export const endpointTestCriteriaApi = {
  listForService: (endpointId: string) =>
    api.get<SuccessResponse<EndpointTestCriterion[]>>(`/endpoints/${endpointId}/test-criteria`),

  createForService: (endpointId: string, data: CreateEndpointTestCriterionInput) =>
    api.post<SuccessResponse<EndpointTestCriterion>>(`/endpoints/${endpointId}/test-criteria`, data),

  update: (id: string, data: UpdateEndpointTestCriterionInput) =>
    api.patch<SuccessResponse<EndpointTestCriterion>>(`/endpoint-test-criteria/${id}`, data),

  delete: (id: string) =>
    api.delete<SuccessResponse<EndpointTestCriterion>>(`/endpoint-test-criteria/${id}`),
};

// ============================================================================
// Named suites - /endpoint-test-suites (task 1180)
// A suite is a stored selection: running one pre-selects its tests in the run
// toolbar, so there is no separate suite execution route.
// ============================================================================
export const endpointTestSuitesApi = {
  list: (endpointId: string) =>
    api.get<SuccessResponse<EndpointTestSuite[]>>(
      `/endpoint-test-suites${buildQuery({ endpointId })}`
    ),

  create: (data: CreateEndpointTestSuiteInput) =>
    api.post<SuccessResponse<EndpointTestSuite>>('/endpoint-test-suites', data),

  update: (id: string, data: UpdateEndpointTestSuiteInput) =>
    api.patch<SuccessResponse<EndpointTestSuite>>(`/endpoint-test-suites/${id}`, data),

  delete: (id: string) =>
    api.delete<SuccessResponse<EndpointTestSuite>>(`/endpoint-test-suites/${id}`),
};

// ============================================================================
// Test Composer API - dynamic endpoint /mw/compose-endpoint-tests (task 364/365/385)
// Generates reviewable test scenarios for an endpoint. Same synchronous
// invoke + collaborationId-continuation contract as the other platform generators —
// no new transport, rides dynamicEndpointsApi.invoke.
// ============================================================================
export const testComposerApi = {
  // Invoke the compose-endpoint-tests generator. Omit collaborationId to start
  // a fresh generation; pass it to continue the same conversation.
  compose: (data: ComposeTestsRequest) =>
    dynamicEndpointsApi.invoke<ComposeTestsResponse>(
      SYSTEM_ACCOUNT_SLUG,
      'compose-endpoint-tests',
      'POST',
      data
    ),
};

// ============================================================================
// Health Check API - /healthcheck
// ============================================================================
export const healthApi = {
  check: () => api.get('/healthcheck'),
  live: () => api.get('/healthcheck/live'),
  info: () => api.get('/healthcheck/info'),
};

// ============================================================================
// Billing API — Stripe Checkout / Billing Portal session creation.
// Routes through innovate-service (the authenticated edge), which proxies
// to AMS. AMS does not validate Keycloak JWTs directly; only innovate-service
// does. When the billing endpoint is not yet deployed innovate-service returns
// 404/501/5xx and the UI degrades gracefully.
// ============================================================================

export const billingApi = {
  /**
   * Create a Stripe Checkout or Billing Portal session.
   * Returns a URL the browser should navigate to.
   * Throws (or rejects with 404/501) when billing is not yet deployed.
   *
   * POST /api/billing/session — proxied through innovate-service.
   * Response shape: { success: boolean, data: { url: string } }
   */
  createSession: (data: BillingSessionRequest) =>
    api.post<BillingSessionResponse>('/billing/session', data),

  /**
   * Fetch the customer-facing plan catalog (task 232).
   * GET /api/billing/plans — proxied through innovate-service from AMS, so the
   * plan cards always render the live catalog, never hardcoded plan data.
   * Response shape: { success: boolean, data: { plans: PlanCatalogPlan[] } }
   */
  getPlans: () => api.get<PlanCatalogResponse>('/billing/plans'),

  /**
   * Change the plan Stripe bills for the account (task 965).
   * POST /api/billing/plan — owner-gated innovate-service proxy for the AMS
   * plan change, which swaps the subscription's plan price with Stripe
   * prorations. Rejects with the service's machine-readable refusal
   * (`no_billed_subscription`, `already_on_plan`, `plan_not_available`,
   * `plan_not_self_serve`) so the page can say which one happened.
   * Response shape: { success: boolean, data: { billedPlan, entitlementPlan } }
   */
  changePlan: (planSlug: string) =>
    api.post<BillingPlanChangeResponse>('/billing/plan', { planSlug }),
};

// ============================================================================
// Consent API — /consent/accept (task 422)
// Records the caller's acceptance of the current Terms of Use + Privacy Policy.
// Proxied through innovate-service (the authenticated edge) to AMS
// PATCH /api/users/by-auth/:authUserId/consent — the browser never calls AMS
// directly, and the authUserId is ALWAYS the token subject resolved
// server-side (never sent from the client). Used by the ConsentGate
// interstitial; a success response is authoritative for the session even
// though fresh tokens may carry consent_pending=true for ~30s (SPI cache).
// ============================================================================
/** Preference categories from the AMS vocabulary (task 459/461). */
export type PreferenceCategory =
  | 'marketing_email'
  | 'product_updates'
  | 'analytics_tracking';

export type PreferenceAction = 'opt_in' | 'opt_out';

/**
 * Current effective opt-in/opt-out state per category, derived server-side
 * from the append-only AMS preference ledger (latest event per category wins,
 * else the category default: marketing categories opt_out, analytics opt_in).
 */
export type PreferenceState = Record<PreferenceCategory, PreferenceAction>;

export const consentApi = {
  /** POST /api/consent/accept — accept the current policy versions. */
  accept: () =>
    api.post<SuccessResponse<{ accepted: boolean }>>('/consent/accept'),

  /**
   * GET /api/consent/preferences — the caller's current preference state
   * (task 461). Subject resolved server-side from the token; read by the
   * preference center and the analytics opt-out gate.
   */
  getPreferences: () =>
    api.get<SuccessResponse<PreferenceState>>('/consent/preferences'),

  /**
   * PUT /api/consent/preferences — record one opt-in/opt-out change
   * (task 461). The ledger `source` is forced to `preference_center`
   * server-side. Responds with the resulting current-state map — the
   * preference center renders THAT (recorded truth), never an optimistic
   * flip.
   */
  updatePreference: (category: PreferenceCategory, action: PreferenceAction) =>
    api.put<SuccessResponse<PreferenceState>>('/consent/preferences', {
      category,
      action,
    }),
};

// ============================================================================
// Profile API — /me/profile (task 596)
// The User's own platform profile. Proxied through innovate-service to AMS
// (GET/PATCH /api/users/by-auth/:authUserId/profile), which is the system of
// record: nothing here is read from the JWT, and the authUserId is ALWAYS the
// token subject resolved server-side (never sent from the client).
// ============================================================================

/**
 * Where the `displayName` a surface renders came from:
 *   - `explicit` the User chose it (a profile edit, or "What should we call you?")
 *   - `name`     derived from the profile's firstName/lastName
 *   - `email`    derived from the email local part (the platform's oldest fallback)
 */
export type DisplayNameSource = 'explicit' | 'name' | 'email';

/**
 * The profile shape the platform returns — identical whether or not the User
 * has ever opened their profile, so no surface special-cases a missing one.
 */
export interface UserProfile {
  displayName: string;
  displayNameSource: DisplayNameSource;
  firstName: string | null;
  lastName: string | null;
  timezone: string | null;
  locale: string | null;
  /**
   * The User's author mark (task 1089): signs their Build comments and
   * Activity Log entries. Never empty — AMS derives a lowercase default from
   * `displayName` when the User has stored none (`initialsSource: 'derived'`).
   */
  initials: string;
  initialsSource: 'explicit' | 'derived';
  attributes: Record<string, unknown> | null;
}

/**
 * Partial profile update. An omitted key is left untouched; an explicit `null`
 * clears the stored value.
 */
export interface UserProfileUpdate {
  firstName?: string | null;
  lastName?: string | null;
  timezone?: string | null;
  locale?: string | null;
  /** 1 to 5 letters or digits (validated by AMS); `null` clears back to derived. */
  initials?: string | null;
  attributesJson?: Record<string, unknown> | null;
}

export const profileApi = {
  /** GET /api/me/profile — the caller's profile. */
  get: () => api.get<SuccessResponse<UserProfile>>('/me/profile'),

  /**
   * PATCH /api/me/profile — partial update, answering with the stored profile.
   * The form renders THAT (recorded truth), never what it optimistically sent:
   * `displayName` is recomputed server-side and may differ from the name the
   * User just typed.
   */
  update: (update: UserProfileUpdate) =>
    api.patch<SuccessResponse<UserProfile>>('/me/profile', update),
};

// ============================================================================
// Realtime API - /realtime
// ============================================================================
export const realtimeApi = {
  status: () => api.get('/realtime/status'),
  token: () => api.get('/realtime/token'),
  resetCircuit: () => api.post('/realtime/circuit/reset'),
};

// ============================================================================
// Account Usage API - /accounts/usage
// Returns the current period's endpoint call count for the authenticated account.
// This endpoint is added by innovate-service as part of task 076 metering work.
// When not yet deployed it will 404; the UI degrades gracefully (no count shown).
// ============================================================================

export interface AccountUsageSummary {
  period: string;
  endpointCallCount: number;
  /**
   * Accrued external LLM dollar cost this period in micro-USD, BEFORE the
   * per-tier markup. The billed usage cost = (rawCostMicroUsd / 1e6) × markup,
   * where the markup multiplier comes from the `model_cost_markup_bps` plan limit
   * in the JWT claims. Optional for backward compatibility with older
   * innovate-service builds that only returned the call count.
   */
  rawCostMicroUsd?: number;
  /**
   * Current deployed-endpoint count for the account. Drives the included-vs-used
   * endpoint display and any extra-endpoint billing shown on the billing page.
   * Optional for backward compatibility.
   */
  deployedEndpoints?: number;
  /**
   * Per-AI-Service slice of this period's raw cost (task 1010), sorted by cost
   * descending and empty when there is no usage. The costs sum exactly to
   * `rawCostMicroUsd` — innovate-service writes the per-endpoint audit row and
   * the account counter in one transaction — so a per-service column built from
   * this array always reconciles to the usage-cost tile beside it. An archived
   * AI Service keeps its usage rows and still appears here, so an id may not be
   * among the listed endpoints.
   *
   * Optional for backward compatibility with innovate-service builds predating
   * task 1010; absent means "cannot compute", not "zero".
   */
  byEndpoint?: { endpointId: string; costMicroUsd: number; callCount: number }[];
  /**
   * The account's current plan slug (task 680), resolved server-side from the
   * live AMS plan resolver. `null` when the account has no plan or the plan
   * could not be resolved — the fail-closed posture, and the signal the no-plan
   * UX keys on now that the `plan` JWT claim is gone (task 676).
   *
   * Optional for backward compatibility with innovate-service builds predating
   * task 680; absent is treated exactly like `null`.
   */
  plan?: string | null;
  /**
   * The plan's limits (task 680), keyed by limit name — e.g.
   * { "max_endpoints": 25, "included_endpoint_calls": -1 }. -1 means unlimited.
   * `null` alongside a `null` plan. Replaces the dropped `limits{}` JWT claim.
   */
  limits?: Record<string, number> | null;
  /**
   * The account's spending limit and where this month stands against it
   * (task 1159; service task 1156). Optional for builds predating task 1156;
   * absent reads as "no limit".
   */
  spending?: AccountSpending | null;
}

export const accountUsageApi = {
  /**
   * GET /api/accounts/usage — current period usage summary for the
   * authenticated account (account_id from JWT scope).
   * Degrades gracefully on 404 (endpoint not yet deployed).
   */
  getCurrentPeriod: () =>
    api.get<{ success: boolean; data: AccountUsageSummary }>('/accounts/usage'),
};

// ============================================================================
// Account Spending API - /accounts/spending (task 1159 -> service task 1156)
//
// The account's monthly spending limit, alert thresholds and hard stop. Reads
// ride the usage summary's `spending` block; the write is owners and admins.
// A refusal past the limit is `402 spending_limit_reached`, which the response
// interceptor hands to the entitlement handler.
// ============================================================================
export const accountSpendingApi = {
  /** PATCH /accounts/spending — save the limit, thresholds and hard stop. */
  update: (data: UpdateAccountSpendingInput) =>
    api.patch<SuccessResponse<AccountSpendingSettings>>('/accounts/spending', data),
};

// ============================================================================
// Custom Domains API - /account/custom-domains (tasks 844 / 850)
//
// Customer-owned hostnames fronting the account's endpoint surface family. AMS
// is the registry of record; innovate-service proxies its CRUD (the browser
// holds no platform role and no service key) and adds the entitlement
// projection the settings section renders its plan gate from.
//
// `remove` takes the HOSTNAME, not an id — that is AMS's addressing, kept as-is
// so the two layers describe a domain the same way.
// ============================================================================
export const customDomainsApi = {
  /** GET /account/custom-domains — the domains and the live plan gate together. */
  list: () => api.get<SuccessResponse<CustomDomainsView>>('/account/custom-domains'),

  /** POST /account/custom-domains — register a hostname (owner/admin). */
  create: (hostname: string) =>
    api.post<SuccessResponse<CustomDomain>>('/account/custom-domains', { hostname }),

  /** DELETE /account/custom-domains/{hostname} — stop serving it (owner/admin). */
  remove: (hostname: string) =>
    api.delete<SuccessResponse<{ hostname: string }>>(
      `/account/custom-domains/${encodeURIComponent(hostname)}`
    ),
};

// ============================================================================
// Account Models API - /account/models (task 1147 -> service task 1146 step 7)
//
// The account's model policy: its own provider keys (Bring Your Own Keys),
// which providers and Models it allows, and its defaults. Reads need only an
// account scope (the model pickers read `allowed` for every member); writes are
// owner/admin. Storing a key is plan-gated (`byok`): a plan without it answers
// 403 feature_not_entitled, which the response interceptor hands to the
// entitlement handler like any other paid capability.
//
// A key is write-only: it leaves in the POST/PUT body and only its last four
// characters ever come back. `:modelId` is the Model row UUID.
// ============================================================================
export const accountModelsApi = {
  /** GET /account/models/providers — enabled providers with the account's key status. */
  listProviders: () =>
    api.get<SuccessResponse<AccountModelProvider[]>>('/account/models/providers'),

  /** POST /account/models/providers/:providerId/key — verify, then store. 400 = failed verification, nothing saved. */
  addKey: (providerId: string, key: string) =>
    api.post<SuccessResponse<AccountProviderKeySaved>>(`/account/models/providers/${providerId}/key`, { key }),

  /** PUT /account/models/providers/:providerId/key — replace the stored key; same verify-then-store flow. */
  replaceKey: (providerId: string, key: string) =>
    api.put<SuccessResponse<AccountProviderKeySaved>>(`/account/models/providers/${providerId}/key`, { key }),

  /** POST /account/models/providers/:providerId/key/verify — re-verify the stored key. */
  verifyKey: (providerId: string) =>
    api.post<SuccessResponse<AccountProviderKeyVerifyResult>>(`/account/models/providers/${providerId}/key/verify`),

  /** DELETE /account/models/providers/:providerId/key — back to the platform key. */
  removeKey: (providerId: string) =>
    api.delete<SuccessResponse<unknown>>(`/account/models/providers/${providerId}/key`),

  /** PATCH /account/models/providers/:providerId — turn a provider on or off for the account. */
  setProviderEnabled: (providerId: string, enabled: boolean) =>
    api.patch<SuccessResponse<unknown>>(`/account/models/providers/${providerId}`, { enabled }),

  /** PATCH /account/models/default-provider — the provider new agents start on. */
  setDefaultProvider: (providerId: string | null) =>
    api.patch<SuccessResponse<{ defaultProviderId: string | null }>>('/account/models/default-provider', { providerId }),

  /** GET /account/models/allowed — catalog Models grouped by provider, with the account's settings. */
  listAllowed: () =>
    api.get<SuccessResponse<AccountAllowedModelGroup[]>>('/account/models/allowed'),

  /**
   * PATCH /account/models/:modelId — allow/disallow or make the provider default.
   * Disallowing a Model agents still use is refused with 409 `MODEL_IN_USE`,
   * `details.usedByAgents`; a default that is not allowed or not reachable with
   * 422 `MODEL_NOT_ALLOWED`.
   */
  updateModel: (modelId: string, data: UpdateAccountModelInput) =>
    api.patch<SuccessResponse<AccountModelPolicyRow>>(`/account/models/${modelId}`, data),

  /** POST /account/models/:modelId/move-agents — repoint this Model's agents, keeping their settings. */
  moveAgents: (modelId: string, targetModelId: string) =>
    api.post<SuccessResponse<MoveAccountModelAgentsResult>>(`/account/models/${modelId}/move-agents`, { targetModelId }),
};

// ============================================================================
// API Keys API - /api-keys (task 123 backend; task 380 Connect tab consumer)
// Mints account-scoped keys for apiKey-mode endpoints. The plaintext `key` is
// returned exactly ONCE by mint() and is never recoverable afterwards.
// ============================================================================

export interface MintedApiKey {
  id: string;
  prefix: string;
  label: string | null;
  /** Plaintext key — present only in the mint response, shown once. */
  key: string;
}

/**
 * A key's metadata, as list/get return it (task 974). Never carries the secret
 * or its hash. There is no `active` field and none is invented here: revocation
 * is a soft delete and the list route returns only the account's active keys,
 * so a key that is listed is by construction an active one.
 */
export interface ApiKey {
  id: string;
  accountId: string;
  prefix: string;
  label: string | null;
  lastUsedAt: string | null;
  createdAt: string;
}

export const apiKeysApi = {
  /** POST /api-keys — mint a new account API key (plaintext returned once). */
  mint: (label?: string) =>
    api.post<SuccessResponse<MintedApiKey>>('/api-keys', label ? { label } : {}),
  /** GET /api-keys — the account's active keys, metadata only. */
  list: (params?: { search?: string; page?: number; limit?: number }) =>
    api.get<PaginatedResponse<ApiKey>>('/api-keys', { params }),
  /** GET /api-keys/:id — one key's metadata. */
  get: (id: string) => api.get<SuccessResponse<ApiKey>>(`/api-keys/${id}`),
  /** DELETE /api-keys/:id — revoke (soft delete); the key stops resolving. */
  revoke: (id: string) => api.delete<SuccessResponse<ApiKey>>(`/api-keys/${id}`),
};

// ============================================================================
// Collaboration Config Validation API - POST /collaboration-configs/validate
// (task 380 Config tab consumer). Validates a definition server-side and
// returns { valid, errors, warnings, analysis }.
// ============================================================================

export interface DefinitionValidationResponse {
  valid: boolean;
  errors: Array<Record<string, unknown>>;
  warnings: Array<Record<string, unknown>>;
  analysis?: Record<string, unknown>;
}

export const collaborationConfigValidationApi = {
  validate: (definition: Record<string, unknown>, checkEntities = true) =>
    api.post<SuccessResponse<DefinitionValidationResponse>>(
      '/collaboration-configs/validate',
      { definition, checkEntities }
    ),
};

// ============================================================================
// Resource Versioning API (task 725)
// ----------------------------------------------------------------------------
// Report: project/reports/innovate-service-published-resource-versioning.md
//
// Publishing freezes a resource's current state as an immutable version; the
// next edit auto-creates the following draft. The whole feature is dormant
// until the Account adopts it, which is why every surface here keys off
// `accountVersioningApi.get()` rather than a feature flag: the flag is the
// rollback lever, the account setting is the user-facing gate.
//
// **Route shape**: every versioning route is nested under its entity's own
// collection (`/endpoints/:id/versions`, `/agents/:id/publish`, …) rather than a
// top-level mount. That is deliberate on the service side: a new first path
// segment would have to join the reserved-segment list that account slugs are
// checked against, or a customer slug could shadow it. `versionCollection`
// below is the single place that mapping lives.
// ============================================================================

/** The URL collection each versioned entity type lives under. */
const VERSION_COLLECTIONS: Record<VersionedEntityType, string> = {
  endpoint: 'endpoints',
  collaboration_config: 'collaboration-configs',
  agent: 'agents',
  prompt: 'prompts',
};

/** Base path for one versioned resource, e.g. `/endpoints/ep-1`. */
const resourcePath = (entityType: VersionedEntityType, entityId: string) =>
  `/${VERSION_COLLECTIONS[entityType]}/${entityId}`;

export const accountVersioningApi = {
  /**
   * GET /api/account/versioning — the Account's adoption state. Degrades to the
   * dormant answer on failure (handled by the hook, not here).
   */
  get: () => api.get<SuccessResponse<AccountVersioningSettings>>('/account/versioning'),

  /**
   * POST /api/account/versioning — adopt the publishing practice.
   * One-way by product rule: the service rejects anything but `enabled: true`
   * with a clear error, so this client offers no way to send anything else.
   */
  enable: () =>
    api.post<SuccessResponse<AccountVersioningSettings>>('/account/versioning', {
      enabled: true,
    }),
};

export const resourceVersionsApi = {
  /** The four-state status the indicator renders, in one call. */
  getStatus: (entityType: VersionedEntityType, entityId: string) =>
    api.get<SuccessResponse<ResourceVersionStatusView>>(
      `${resourcePath(entityType, entityId)}/versions/status`
    ),

  /** Version history, newest first. */
  list: (entityType: VersionedEntityType, entityId: string) =>
    api.get<SuccessResponse<ResourceVersionSummary[]>>(
      `${resourcePath(entityType, entityId)}/versions`
    ),

  /**
   * One version whole, including its frozen document. The version travels as
   * two integer path segments, never a decimal string — `1.10` is the tenth
   * minor of major 1, and a float would silently make it the first.
   */
  get: (entityType: VersionedEntityType, entityId: string, major: number, minor: number) =>
    api.get<SuccessResponse<ResourceVersionDetail>>(
      `${resourcePath(entityType, entityId)}/versions/${major}/${minor}`
    ),

  /**
   * Dry run: the version this resource would take, whether it is the first
   * publish, what would cascade, and (for an endpoint) what the published
   * version would pin. `majorBump` changes the answer, so it is part of the
   * request rather than something the dialog computes.
   */
  previewPublish: (
    entityType: VersionedEntityType,
    entityId: string,
    majorBump = false
  ) =>
    api.post<SuccessResponse<PublishPreview>>(
      `${resourcePath(entityType, entityId)}/publish/preview`,
      { majorBump }
    ),

  /**
   * Freeze the working version, cascading to dirty dependencies.
   *
   * `environment` on the request is a *request*, not an instruction: the service
   * decides where the new version actually lands by the shape of the Account,
   * and reports it back on `PublishResult.environment` (with
   * `environmentIgnored` when a named environment did not exist). Read the
   * result, never the request, to say what happened.
   */
  publish: (entityType: VersionedEntityType, entityId: string, data: PublishRequest = {}) =>
    api.post<SuccessResponse<PublishResult>>(
      `${resourcePath(entityType, entityId)}/publish`,
      data
    ),
};

// ============================================================================
// Deployment environments and deployments - /environments, /endpoints/:id/deployments
// ----------------------------------------------------------------------------
// The pointer layer over published versions (task 760). An environment is a tag
// pointer; a deployment repoints it at a published version. Promotion and
// rollback are the same call — `createDeployment` — because the only difference
// between them is which version is named.
// ============================================================================

export const environmentsApi = {
  /** The Account's deployment environments, already in promotion order. */
  getAll: () => api.get<SuccessResponse<Environment[]>>('/environments'),

  getById: (id: string) => api.get<SuccessResponse<Environment>>(`/environments/${id}`),

  create: (data: CreateEnvironmentInput) =>
    api.post<SuccessResponse<Environment>>('/environments', data),

  /**
   * Rename, reorder, or make an environment the default. Default is exclusive:
   * setting one clears the previous default server-side, so the caller never
   * has to clear it first.
   */
  update: (id: string, data: UpdateEnvironmentInput) =>
    api.patch<SuccessResponse<Environment>>(`/environments/${id}`, data),

  delete: (id: string) => api.delete<SuccessResponse<Environment>>(`/environments/${id}`),

  /**
   * What this deployment environment carries (task 870). `blocking` names the
   * AI Services deployed on it right now — a non-empty one means DELETE answers
   * `409 IN_USE` — while `dependents.endpoints` names every service with any
   * deployment history on the lane. `info` is the service's own prose about
   * what else the removal touches.
   */
  getDependents: (id: string) =>
    api.get<SuccessResponse<DependentsResponse>>(`/environments/${id}/dependents`),
};

export const deploymentsApi = {
  /** What each environment serves for this AI Service right now — one row per environment. */
  current: (endpointId: string) =>
    api.get<SuccessResponse<Deployment[]>>(`/endpoints/${endpointId}/deployments`),

  /** The full rollout history, newest first. The promotion arrows are drawn from this. */
  history: (endpointId: string, environmentId?: string) =>
    api.get<SuccessResponse<Deployment[]>>(
      `/endpoints/${endpointId}/deployments/history${buildQuery({ environmentId })}`
    ),

  /** Point an environment at a published version — promote and roll back alike. */
  create: (endpointId: string, data: CreateDeploymentInput) =>
    api.post<SuccessResponse<Deployment>>(`/endpoints/${endpointId}/deployments`, data),
};

// ============================================================================
// AI Service analytics - /endpoints/:id/analytics (tasks 1254, 1255)
// ============================================================================

/**
 * The per-day series behind an AI Service's Analytics tab: runs, duration,
 * tokens and cost, error rate, criteria pass rate, and the publish and release
 * annotations. Account-scoped by the service; the range is capped at 90 days.
 */
export const endpointAnalyticsApi = {
  get: (endpointId: string, query: EndpointAnalyticsQuery) =>
    api.get<SuccessResponse<EndpointAnalytics>>(
      `/endpoints/${endpointId}/analytics${buildQuery({ ...query })}`
    ),
};

// ============================================================================
// Artifacts API - /artifacts (task 828)
// ============================================================================

/**
 * Artifacts — the files a collaboration produced (tasks 825–827).
 *
 * Two things about this surface are unlike the rest of this module:
 *
 *   1. **One endpoint, two lenses.** `GET /artifacts` is the account-wide list;
 *      pass `resourceId` and the same call narrows to one resource's artifacts.
 *      The service deliberately did not build a second route, so neither does
 *      this — one shape that cannot drift.
 *   2. **The download is not JSON.** Every other call here unwraps
 *      `SuccessResponse<T>`; the content endpoint answers with the file itself,
 *      under its own content type, so it is read as a blob. It still travels
 *      through this axios instance rather than a bare URL, because the request
 *      interceptor is what attaches the bearer token — a plain `<a href>` or
 *      `window.open` at that URL would arrive unauthenticated.
 */
export const artifactsApi = {
  getAll: (filters?: ArtifactFilters) =>
    api.get<SuccessResponse<ArtifactListResponse>>(`/artifacts${buildQuery({ ...filters })}`),

  getById: (artifactId: string) =>
    api.get<SuccessResponse<Artifact>>(`/artifacts/${artifactId}`),

  /** One version's metadata, addressed by its 1-based number. */
  getVersion: (artifactId: string, versionNumber: number) =>
    api.get<SuccessResponse<ArtifactVersionDetail>>(
      `/artifacts/${artifactId}/versions/${versionNumber}`
    ),

  /** The bytes of one version, as stored. */
  getVersionContent: (artifactId: string, versionNumber: number) =>
    api.get<Blob>(`/artifacts/${artifactId}/versions/${versionNumber}/content`, {
      responseType: 'blob',
    }),

  /** Soft-delete: the artifact and its versions leave every listing. */
  delete: (artifactId: string) =>
    api.delete<SuccessResponse<Artifact>>(`/artifacts/${artifactId}`),
};

// ============================================================================
// Archive API - /archive (task 862)
// ----------------------------------------------------------------------------
// The account's deletion ledger: one entry per User-initiated delete, newest
// first, unrestored only. **The `id` on an entry is the LEDGER entry id, not the
// resource id** — it is what the restore route takes, and it is what makes a
// restore reverse the exact set of rows the delete tombstoned.
//
// `restore` refuses in three named ways, all `409`/`422` and all carrying the
// detail the Archive page renders: `NAME_IN_USE` (something live has taken the
// name back), `DEPENDENCY_ARCHIVED` (it would come back pointing at something
// still deleted), and a `422` for a type that is not restorable.
// ============================================================================
export const archiveApi = {
  list: () => api.get<SuccessResponse<ArchiveEntry[]>>('/archive'),

  restore: (deletionId: string) =>
    api.post<SuccessResponse<unknown>>(`/archive/${deletionId}/restore`),
};

// ============================================================================
// Schedules API - /schedules (task 1000's routes, task 1003's surface)
//
// A schedule is an AI Service's own trigger: a saved input plus a time rule. The
// routes are account-scoped management routes like every other entity here —
// `endpointId` is a FILTER on the list, not part of the path, because a
// schedule belongs to the Account and names the service it runs.
//
// The plan refusals (`402 limit_exceeded` for `max_schedules` and
// `min_schedule_interval_seconds`, `402 no_active_plan`, `403
// feature_not_entitled` for `innovate.schedules`) come back in the shapes the
// response interceptor above already recognises, so they reach the
// EntitlementGuard without anything schedule-specific in the transport.
// ============================================================================
export const schedulesApi = {
  /** The account's schedules, newest first; `endpointId` narrows to one service. */
  list: (filters: ScheduleFilters = {}) =>
    api.get<PaginatedResponse<Schedule>>(`/schedules${buildQuery({ ...filters })}`),

  getById: (id: string) => api.get<SuccessResponse<Schedule>>(`/schedules/${id}`),

  create: (data: CreateScheduleInput) =>
    api.post<SuccessResponse<Schedule>>('/schedules', data),

  /** Edit, pause (`status: 'paused'`) or resume (`status: 'active'`). */
  update: (id: string, data: UpdateScheduleInput) =>
    api.patch<SuccessResponse<Schedule>>(`/schedules/${id}`, data),

  delete: (id: string) => api.delete<SuccessResponse<Schedule>>(`/schedules/${id}`),

  /**
   * The schedule's run log, newest occurrence first. A row with an
   * `endpointRequestId` became a call; a row without one is a skip, which is
   * why skips are here and never in the AI Service's run history.
   */
  listRuns: (id: string, page = 1, limit = 20) =>
    api.get<PaginatedResponse<ScheduleRun>>(`/schedules/${id}/runs${buildQuery({ page, limit })}`),
};

export default api;
